import axios, { AxiosResponse } from "axios";
import * as md5 from "js-md5";
import { LtProxy } from "./appChannel";
import utils from "./utils";
// 直接从 i18next 获取语言，避免 request → i18n/utils → i18n/index → utils → request 循环依赖
import i18next from "i18next";
const getCurrentLanguage = (): string => i18next.language || "en";
import { setGlobalValue } from "../stores/GlobalState";
// API响应基础类型 - 方案1：使用泛型参数区分
export interface ApiResponse<T = any, IsNested = false> {
  status: number;
  message?: string;
  data: IsNested extends true
    ? {
        status: number;
        maxAge?: number;
        time?: number;
        message?: string;
        model: T;
        [key: string]: any;
      }
    : T;
  model?: T; // 兼容旧接口
  time?: number;
  maxAge?: number;
  user?: any;
  [key: string]: any;
}

// 判断是否是嵌套数据
export function isNestedData<T>(
  data: any
): data is { model: T; [key: string]: any } {
  return data && typeof data === "object" && "model" in data;
}

// 获取实际数据的辅助函数，当data是嵌套数据时，返回model
export function getResponseData<T, IsNested = false>(
  response: ApiResponse<T, IsNested>
): T {
  if (isNestedData<T>(response.data)) {
    return response.data.model;
  }
  return response.data as T;
}

// API错误类型
export interface ApiError {
  status: number;
  message: string;
  code?: string;
  time?: number;
}

// 请求配置接口
export interface RequestConfig {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  url?: string;
  data?: unknown;
  params?: unknown;
  alert?: boolean; // 是否显示错误提示
  ignore?: boolean; // 是否忽略错误
  noLogin?: boolean; // 是否需要登录
  headers?: Record<string, string>;
  timeout?: number;
  retry?: number;
  cache?: boolean;
  cacheTime?: number;
}

// 请求选项接口 - 兼容utils中的RequestOptions
export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  alert?: boolean;
  ignore?: boolean;
  noLogin?: boolean;
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
}

// 请求响应接口 - 兼容utils中的RequestResponse
export interface RequestResponse {
  status: number;
  message?: string;
  time?: number;
  user?: any;
  [key: string]: any;
}

// 缓存键生成器
const generateCacheKey = (url: string, params?: any): string => {
  const paramStr = params ? JSON.stringify(params) : "";
  return `${url}${paramStr}`;
};

// 内存缓存
const cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

// 缓存管理
export const cacheManager = {
  get: (key: string) => {
    const item = cache.get(key);
    if (!item) return null;

    const now = Date.now();
    if (now - item.timestamp > item.ttl) {
      cache.delete(key);
      return null;
    }

    return item.data;
  },

  set: (key: string, data: any, ttl: number = 5 * 60 * 1000) => {
    cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  },

  clear: (pattern?: string) => {
    if (pattern) {
      for (const key of Array.from(cache.keys())) {
        if (key.includes(pattern)) {
          cache.delete(key);
        }
      }
    } else {
      cache.clear();
    }
  },
};

// 请求参数处理工具类
class RequestParamProcessor {
  // 移除URL中的token参数
  removeToken(url: string): string {
    while (true) {
      if (url.indexOf("&token=") > 0) {
        url = url.replace(/[&]token=[^&]*/g, "");
      } else if (url.indexOf("?token=") > 0) {
        url = url.replace(/[?]token=[^&]*/g, "?");
        url = url.replace("?&", "?");
      } else {
        break;
      }
    }
    if (url[url.length - 1] === "?") {
      url = url.substring(0, url.length - 1);
    }
    return url;
  }

  // 为URL添加token
  appendToken(url: string): string {
    url = this.removeToken(url);
    const token = utils.getOnceToken();
    if (token) {
      url = this.appendUrl(url, "token", token);
    }

    const language = getCurrentLanguage();
    if (language) {
      url = this.appendUrl(url, "lang", language);
    }
    const device = utils.deviceIsMobile() ? "mobile" : "pc";
    url = this.appendUrl(url, "deviceType", device);
    return url;
  }

  // 为URL添加参数
  appendUrl(url: string, name: string, data: string): string {
    const i = url.indexOf("?");
    if (i > 0) {
      url += "&" + name + "=" + encodeURIComponent(data);
    } else {
      url += "?" + name + "=" + encodeURIComponent(data);
    }
    return url;
  }

  // 合并对象
  combineObject(obj1: any, obj2: any): any {
    if (!obj2) {
      return obj1;
    }
    if (!obj1) {
      obj1 = {};
    }
    for (const k in obj2) {
      const v = obj2[k];
      if (!v) {
        obj1[k] = v;
      } else if (v instanceof Array) {
        const arr = v;
        if (!obj1[k]) {
          obj1[k] = [];
        }
        {
          obj1[k].length = arr.length;
          const arrNew = obj1[k];
          for (let i = 0; i < arr.length; i++) {
            if (typeof arr[i] !== "object") {
              arrNew[i] = arr[i];
            } else {
              arrNew[i] = this.combineObject(arrNew[i], arr[i]);
            }
          }
        }
      } else if (typeof v === "object") {
        if (v.__map) {
          obj1[k] = v;
        } else {
          obj1[k] = this.combineObject(obj1[k], v);
        }
      } else {
        obj1[k] = v;
      }
    }
    return obj1;
  }

  // 合并多个对象
  combineObjects(...args: any[]): any {
    let ret = args[0];
    for (let i = 1; i < args.length; i++) {
      const obj = args[i];
      ret = this.combineObject(ret, obj);
    }
    return ret;
  }
}

// 创建参数处理器实例
const paramProcessor = new RequestParamProcessor();

// Header生成器类
export class HeaderGenerator {
  /**
   * 生成完整的请求头
   * @param url 请求URL（用于生成安全令牌）
   * @param customHeaders 自定义头部（可选）
   * @returns 完整的请求头对象
   */
  static generateHeaders(
    url: string = "",
    customHeaders: Record<string, string> = {}
  ): Record<string, string> {
    const headers: Record<string, string> = {};

    // 获取用户信息
    const user = JSON.parse(utils.getUser() || "null");

    // 设置基础请求头
    headers["Device"] = utils.deviceIsMobile() ? "MOBILE" : "PC";
    headers["Cache-Control"] = "no-cache";
    headers["Pragma"] = "no-cache";

    // 添加认证 token
    if (user && user.token && user.token !== "undefined") {
      headers["Auth"] = user.token;
    }

    // 添加应用相关头部
    if (utils.isApp()) {
      headers["APP"] = utils.isGpApp() ? "2" : "1";
      const version = utils.getAppVersion();
      if (version) {
        headers["VERSION"] = version;
      }
    }

    // 添加设备指纹
    const fp = utils.getObject("BrowserFingerprintId");
    if (fp) {
      headers["BFID"] = fp;
    }

    const auuid = utils.getObject("__auuid");
    if (auuid) {
      headers["AUUID"] = auuid;
    }
    // 添加时间戳和安全令牌
    const serverTime = utils.getServerTime();
    headers["ST"] = serverTime.toString();
    headers["TZ"] = (-(
      new Date(serverTime).getTimezoneOffset() / 60
    )).toString();

    // 生成安全令牌
    if (url) {
      const x = url.indexOf("?");
      const xx = x > 0 ? url.substring(0, x) : url;
      const sss = "#kfdjksgjdksajgkdsjkdjfkda#" + xx + "#" + serverTime;
      headers["STT"] = md5.md5(sss);
    }

    // 添加语言
    const language = getCurrentLanguage();
    if (language) {
      headers["LANG"] = language;
    } else {
      headers["LANG"] = utils.getBrowserLanguage();
    }

    // 添加国家
    const countryId = utils.getObject("countryId");
    if (countryId) {
      headers["COUNTRY"] = countryId;
    }

    // 添加导航令牌
    const navToken = utils.getObject("navToken");
    if (navToken) {
      headers["NAV_TOKEN"] = navToken;
    }

    // 添加 GAID 和 AID
    const gaid = utils.getObject("gaid");
    if (gaid) {
      headers["GAID"] = gaid;
    }
    const aid = utils.getObject("aid");
    if (aid) {
      headers["AID"] = aid;
    }

    // 添加 TKK
    const tkk = utils.getObject("tkk");
    if (tkk) {
      headers["Tkk"] = tkk;
    }
    if (utils.isApp()) {
      headers["APP"] = utils.isGpApp() ? "2" : "1";
      const version = utils.getAppVersion();
      if (version) {
        headers["VERSION"] = version;
      }
    } else if (utils.updateCheckIsPwa()) {
      headers["pwa"] = "1";
    }
    const ch = window.__ch || utils.getObject("ch");
    if (ch) {
      headers["ch"] = ch;
    } else {
      const googleApkUtmParam = utils.getObject("googleApkUtmParam");
      if (googleApkUtmParam) {
        const match = googleApkUtmParam.match(/utm_source=([^&]+)/);
        const source = match ? match[1] : null;
        if (source) {
          headers["ch"] = source;
        }
      }
    }

    // 合并自定义头部
    Object.assign(headers, customHeaders);

    return headers;
  }

  /**
   * 生成基础请求头（不包含安全令牌）
   * @param customHeaders 自定义头部（可选）
   * @returns 基础请求头对象
   */
  static generateBasicHeaders(
    customHeaders: Record<string, string> = {}
  ): Record<string, string> {
    return this.generateHeaders("", customHeaders);
  }

  /**
   * 生成认证相关的头部
   * @returns 认证头部对象
   */
  static async generateAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {};

    // 获取用户信息
    const user = JSON.parse(utils.getUser() || "null");

    // 添加认证 token
    if (user && user.token && user.token !== "undefined") {
      headers["Auth"] = user.token;
    }

    // 添加设备指纹
    if (LtProxy.isActive()) {
      const cc = await LtProxy.getConfig();
      if (cc) {
        if (cc.gaid) {
          headers["GAID"] = cc.gaid;
        }
        if (cc.afId) {
          headers["AFID"] = cc.afId;
        }
        if (cc.androidId) {
          headers["AID"] = cc.androidId;
        }
      }
    } else {
      const gaid = utils.getObject("gaid");
      if (gaid) {
        headers["GAID"] = gaid;
      }
      const aid = utils.getObject("aid");
      if (aid) {
        headers["AID"] = aid;
      }
    }
    const fp = utils.getObject("BrowserFingerprintId");
    if (fp) {
      headers["BFID"] = fp;
    }

    // 添加导航令牌
    const navToken = utils.getObject("navToken");
    if (navToken) {
      headers["NAV_TOKEN"] = navToken;
    }

    return headers;
  }

  /**
   * 生成设备相关的头部
   * @returns 设备头部对象
   */
  static generateDeviceHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    // 设置设备类型
    headers["Device"] = utils.deviceIsMobile() ? "MOBILE" : "PC";

    // 添加应用相关头部
    if (utils.isApp()) {
      headers["APP"] = utils.isGpApp() ? "2" : "1";
      const version = utils.getAppVersion();
      if (version) {
        headers["VERSION"] = version;
      }
    }

    // 添加设备指纹
    const fp = utils.getObject("BrowserFingerprintId");
    if (fp) {
      headers["BFID"] = fp;
    }

    return headers;
  }

  /**
   * 生成语言和地区相关的头部
   * @returns 语言和地区头部对象
   */
  static generateLocaleHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    // 添加语言
    const language = getCurrentLanguage();
    if (language) {
      headers["LANG"] = language;
    } else {
      headers["LANG"] = utils.getBrowserLanguage();
    }

    // 添加国家
    const countryId = utils.getObject("countryId");
    if (countryId) {
      headers["COUNTRY"] = countryId;
    }

    return headers;
  }

  /**
   * 生成时间戳和安全相关的头部
   * @param url 请求URL（用于生成安全令牌）
   * @returns 时间戳和安全头部对象
   */
  static generateSecurityHeaders(url: string = ""): Record<string, string> {
    const headers: Record<string, string> = {};

    // 添加时间戳
    const serverTime = utils.getServerTime();
    headers["ST"] = serverTime.toString();
    headers["TZ"] = (-(
      new Date(serverTime).getTimezoneOffset() / 60
    )).toString();

    // 生成安全令牌
    if (url) {
      const x = url.indexOf("?");
      const xx = x > 0 ? url.substring(0, x) : url;
      const sss = "#kfdjksgjdksajgkdsjkdjfkda#" + xx + "#" + serverTime;
      headers["STT"] = md5.md5(sss);
    }

    return headers;
  }
}

// 创建 axios 实例
const axiosInstance = axios.create({
  timeout: 10000,
  headers: {
    "Content-Type": "application/json; charset=utf-8",
    Accept: "application/json",
  },
});

// 请求拦截器
axiosInstance.interceptors.request.use(
  async config => {
    const url = config.url || "";

    // 使用HeaderGenerator生成完整的请求头
    const headers = HeaderGenerator.generateHeaders(url);

    // 设置所有头部
    Object.entries(headers).forEach(([key, value]) => {
      config.headers.set(key, value);
    });

    // 处理加密请求
    if (utils.isCryptoRequest(url)) {
      utils.cryptoRequest(config);
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 响应拦截器
axiosInstance.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const data = response.data;
    const config = response.config;
    // 处理服务器时间同步
    if (data && data.time && !config.url?.includes("apicdn")) {
      const localTime = new Date().getTime();
      const serverTime = data.time;
      const timeDiff = Math.abs(localTime - serverTime);
      if (timeDiff > 3000) {
        window._serverUpdateTime1 = serverTime;
        window.__serverTimeSpan = serverTime - localTime;
        window.__serverTime = function () {
          return new Date().getTime() + (window.__serverTimeSpan || 0);
        };
      }
    }

    // 处理用户信息更新
    if (
      data &&
      (data as any).user &&
      ((data as any).user as { token?: string }).token
    ) {
      utils.setUser((data as any).user);
    }
    const status = (data as any).status;
    if (status !== 0) {
      if (status === 1012) {
        window.dispatchEvent(
          new CustomEvent("navigateToLogin", {
            detail: { reason: "session_expired" },
          })
        );
      }
      throw new Error((data as any).message || `API error (status: ${status})`);
    }
    return response;
  },
  (error: unknown) => {
    // 统一错误处理
    const axiosError = error as {
      response?: {
        status?: number;
        data?: { message?: string; code?: string; status?: number };
      };
      message?: string;
    };
    const status = axiosError.response?.status;
    const data = axiosError.response?.data;

    // 403 且 code 为 FORBIDDEN_BFID：当前设备被拉入黑名单，统一替换为拦截页
    if (
      status === 403 &&
      data?.status === 403 &&
      data?.code === "FORBIDDEN_BFID"
    ) {
      setGlobalValue("deviceBlacklisted", true);
    }

    const errorResponse: ApiResponse<any, false> = {
      status: status || -1,
      data: null,
      time: Date.now(),
      maxAge: 0,
      model: null,
      message: data?.message || axiosError.message || "Network Error",
    };
    return Promise.reject(errorResponse);
  }
);

// 响应数据验证和处理
class ResponseProcessor {
  // 验证响应数据格式
  validateResponse(data: any): boolean {
    if (!data || typeof data !== "object") {
      return false;
    }

    // 检查必要字段
    if (typeof data.status !== "number") {
      return false;
    }

    return true;
  }

  // 处理响应数据
  processResponse<T, IsNested = false>(
    data: ApiResponse<T, IsNested>
  ): ApiResponse<T, IsNested> {
    if (!this.validateResponse(data)) {
      throw new Error("Invalid response format");
    }

    // 处理用户信息更新
    if (
      data &&
      (data as any).user &&
      ((data as any).user as { token?: string }).token
    ) {
      utils.setUser((data as any).user);
    }

    return data;
  }

  // 处理错误响应
  processError(error: unknown): RequestResponse {
    const axiosError = error as {
      response?: { status?: number; data?: { message?: string } };
      message?: string;
    };

    return {
      status: axiosError.response?.status || -1,
      time: Date.now(),
      message:
        axiosError.response?.data?.message ||
        axiosError.message ||
        "Network Error",
    };
  }
}

// 创建响应处理器实例
const responseProcessor = new ResponseProcessor();

// 基础API类
export class BaseApi {
  protected baseURL: string;

  constructor(baseURL: string = "") {
    this.baseURL = baseURL;
  }

  // GET 请求
  protected async get<T = unknown, IsNested = false>(
    url: string,
    params?: unknown,
    options: RequestConfig = {}
  ): Promise<ApiResponse<T, IsNested>> {
    const fullUrl = this.baseURL + url;
    return apiRequest<T, IsNested>(fullUrl, params, {
      ...options,
      method: "GET",
    });
  }

  // POST 请求
  protected async post<T = unknown, IsNested = false>(
    url: string,
    data?: unknown,
    options: RequestConfig = {}
  ): Promise<ApiResponse<T, IsNested>> {
    const fullUrl = this.baseURL + url;
    return apiRequest<T, IsNested>(fullUrl, data, {
      ...options,
      method: "POST",
    });
  }

  // PUT 请求
  protected async put<T = unknown, IsNested = false>(
    url: string,
    data?: unknown,
    options: RequestConfig = {}
  ): Promise<ApiResponse<T, IsNested>> {
    const fullUrl = this.baseURL + url;
    return apiRequest<T, IsNested>(fullUrl, data, {
      ...options,
      method: "PUT",
    });
  }

  // DELETE 请求
  protected async del<T = unknown, IsNested = false>(
    url: string,
    data?: unknown,
    options: RequestConfig = {}
  ): Promise<ApiResponse<T, IsNested>> {
    const fullUrl = this.baseURL + url;
    return apiRequest<T, IsNested>(fullUrl, data, {
      ...options,
      method: "DELETE",
    });
  }

  // GET请求带缓存
  protected async getCache<T, IsNested = false>(
    url: string,
    params?: any,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T, IsNested>> {
    const fullUrl = this.baseURL + url;
    const cacheKey = generateCacheKey(fullUrl, params);

    // 检查缓存
    if (config.cache !== false) {
      const cached = cacheManager.get(cacheKey);
      if (cached) {
        return cached;
      }
    }

    try {
      const response = await apiRequest<T, IsNested>(fullUrl, params, {
        ...config,
        method: "GET",
      });

      const data = (response as any).data || response;

      // 缓存数据
      if (config.cache !== false) {
        cacheManager.set(cacheKey, data, config.cacheTime);
      }

      return data;
    } catch (error) {
      console.error(`API GET Error [${fullUrl}]:`, error);
      throw this.handleError(error);
    }
  }

  // 错误处理
  private handleError(error: any): ApiError {
    if (error.status) {
      return {
        status: error.status,
        message: error.message || "request failed",
        code: error.code,
        time: error.time,
      };
    }

    return {
      status: -1,
      message: error.message || "network error",
      code: "NETWORK_ERROR",
      time: Date.now(),
    };
  }
}

// 通用请求方法 - 兼容原有接口
export const apiRequest = async <T = unknown, IsNested = false>(
  url: string,
  data?: unknown,
  options: RequestConfig = {}
): Promise<ApiResponse<T, IsNested>> => {
  try {
    const config: RequestConfig = {
      method: "POST",
      url: url,
      data,
      alert: true,
      ...options,
    };

    const response = await axiosInstance(config);
    return responseProcessor.processResponse<T, IsNested>(response);
  } catch (error: unknown) {
    console.error("API Error:", error);
    const apiError = error as { status?: number; message?: string };
    if (apiError.status === 401) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("xx-user-to");
    }

    if (options.alert !== false) {
      console.error("API Error:", apiError.message);
    }

    throw error;
  }
};

// 兼容utils的请求方法
export const request = async <T = unknown>(
  url: string,
  data?: unknown,
  options: RequestOptions = {}
): Promise<RequestResponse> => {
  try {
    // 转换RequestOptions到RequestConfig
    const config: RequestConfig = {
      method: options.method || "POST",
      url,
      data: data || options.body,
      alert: options.alert,
      ignore: options.ignore,
      noLogin: options.noLogin,
      headers: options.headers,
      timeout: options.timeout,
    };

    const response = await apiRequest<T>(url, data, config);

    // 转换为RequestResponse格式
    const { status, message, time, ...rest } = response;
    return {
      status,
      message,
      time,
      user: (response as any).user,
      ...rest,
    };
  } catch (error: unknown) {
    return responseProcessor.processError(error);
  }
};

// 创建API实例的工厂函数
export const createApi = <T extends BaseApi>(ApiClass: new () => T): T => {
  return new ApiClass();
};

// 导出工具方法
export const removeToken = (url: string) => paramProcessor.removeToken(url);
export const appendToken = (url: string) => paramProcessor.appendToken(url);
export const appendUrl = (url: string, name: string, data: string) =>
  paramProcessor.appendUrl(url, name, data);
export const combineObject = (obj1: any, obj2: any) =>
  paramProcessor.combineObject(obj1, obj2);
export const combineObjects = (...args: any[]) =>
  paramProcessor.combineObjects(...args);

// 导出Header生成器相关方法
export const generateHeaders = (
  url: string = "",
  customHeaders: Record<string, string> = {}
) => HeaderGenerator.generateHeaders(url, customHeaders);
export const generateBasicHeaders = (
  customHeaders: Record<string, string> = {}
) => HeaderGenerator.generateBasicHeaders(customHeaders);
export const generateAuthHeaders = async () =>
  await HeaderGenerator.generateAuthHeaders();
export const generateDeviceHeaders = () =>
  HeaderGenerator.generateDeviceHeaders();
export const generateLocaleHeaders = () =>
  HeaderGenerator.generateLocaleHeaders();
export const generateSecurityHeaders = (url: string = "") =>
  HeaderGenerator.generateSecurityHeaders(url);

export default request;
